import { LearningGoalTheme } from '../../types';

/** Display labels for the four Aistéar 2024 themes, in their canonical order. */
export const THEME_LABELS: Record<LearningGoalTheme, string> = {
  WELLBEING: 'Wellbeing',
  IDENTITY_BELONGING: 'Identity & Belonging',
  COMMUNICATING: 'Communicating',
  EXPLORING_THINKING: 'Exploring & Thinking',
};

/** Canonical theme order for grouped rendering. */
export const THEME_ORDER: LearningGoalTheme[] = [
  'WELLBEING',
  'IDENTITY_BELONGING',
  'COMMUNICATING',
  'EXPLORING_THINKING',
];

/**
 * Per-theme accent colours (Tailwind tokens) used by the coverage summary
 * cards / progress rings. Mirrors the NCCA Aistéar theme palette loosely:
 * Wellbeing=purple, Identity=pink/rose, Communicating=amber, Exploring=green.
 */
export const THEME_COLORS: Record<
  LearningGoalTheme,
  { ring: string; bg: string; text: string; bar: string }
> = {
  WELLBEING: {
    ring: 'text-purple-500',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    bar: 'bg-purple-500',
  },
  IDENTITY_BELONGING: {
    ring: 'text-rose-500',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    bar: 'bg-rose-500',
  },
  COMMUNICATING: {
    ring: 'text-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    bar: 'bg-amber-500',
  },
  EXPLORING_THINKING: {
    ring: 'text-green-500',
    bg: 'bg-green-50',
    text: 'text-green-700',
    bar: 'bg-green-500',
  },
};

export interface AimGroup<G> {
  aimNumber: number;
  aimText: string;
  goals: G[];
}

export interface ThemeGroup<G> {
  theme: LearningGoalTheme;
  aims: AimGroup<G>[];
}

/**
 * Group a flat list of goal-like records into theme → aim → goals, preserving
 * the canonical theme order and ordering aims/goals by their numeric index.
 * `aimText` is optional on the item (coverage rows don't carry it).
 */
export function groupByThemeAndAim<
  G extends { theme: LearningGoalTheme; aimNumber: number; goalIndex: number; aimText?: string }
>(items: G[]): ThemeGroup<G>[] {
  const byTheme = new Map<LearningGoalTheme, Map<number, AimGroup<G>>>();

  for (const item of items) {
    let aims = byTheme.get(item.theme);
    if (!aims) {
      aims = new Map();
      byTheme.set(item.theme, aims);
    }
    let aim = aims.get(item.aimNumber);
    if (!aim) {
      aim = {
        aimNumber: item.aimNumber,
        aimText: item.aimText ?? `Aim ${item.aimNumber}`,
        goals: [],
      };
      aims.set(item.aimNumber, aim);
    }
    aim.goals.push(item);
  }

  return THEME_ORDER.filter((theme) => byTheme.has(theme)).map((theme) => {
    const aims = Array.from(byTheme.get(theme)!.values()).sort((a, b) => a.aimNumber - b.aimNumber);
    for (const aim of aims) {
      aim.goals.sort((a, b) => a.goalIndex - b.goalIndex);
    }
    return { theme, aims };
  });
}
